/**
 * Flat translation dictionaries — Phase 18h.
 *
 * Key convention: dot-notation, grouped by surface (``nav.*``,
 * ``bell.*``, ``common.*``). Missing keys fall back to English in
 * ``LanguageContext``; English is always the floor so a half-
 * translated string never blocks the UI.
 *
 * Scope: we intentionally translate only the chrome visible on
 * every screen (navbar dropdowns, bell, common buttons) plus the
 * notification surfaces (inbox + preferences). Domain screens stay
 * English until there's real demand — translating forms like
 * "Listing Wizard Step 3" is a much bigger lift and isn't what
 * the phone-first SaaS user actually reads most.
 */

import sidebarOverlays from './sidebar_translations';
// NOTE: the large per-page overlay (`page_translations`, ~7 MB) is
// deliberately NOT imported here — importing it eagerly pulled it into
// the main JS bundle on every page. It is code-split and loaded
// asynchronously by LanguageContext instead; until it arrives, `t()`
// falls back to the inline English default each call site already
// passes, so nothing blocks render. See `loadPageOverlays` below.

const EN = {
  // Navbar — user dropdown
  'nav.myProfile': 'My Profile',
  'nav.myProfile.sub': 'View personal profile details.',
  'nav.changePassword': 'Change Password',
  'nav.changePassword.sub': 'Update your login credentials.',
  'nav.preferences': 'Preferences',
  'nav.preferences.sub': 'Notification & display settings.',
  'nav.signOut': 'Sign out',
  'nav.search': 'Search here...',
  'nav.role.member': 'Member',
  'nav.language': 'Language',

  // Notification bell + inbox
  'bell.title': 'Notifications',
  'bell.empty': 'No notifications yet',
  'bell.markAllRead': 'Mark all read',
  'bell.viewAll': 'View all notifications',
  'bell.connected': 'Live',
  'bell.disconnected': 'Offline',
  'bell.unreadBadge': 'unread',

  // Notification center
  'inbox.title': 'Notification Center',
  'inbox.subtitle': 'All notifications in one place',
  'inbox.filter.all': 'All',
  'inbox.filter.unread': 'Unread',
  'inbox.filter.read': 'Read',
  'inbox.deleteSelected': 'Delete selected',
  'inbox.selectAll': 'Select all',
  'inbox.empty': 'You have no notifications',

  // Preferences
  'prefs.title': 'Notification Preferences',
  'prefs.subtitle': 'Choose how you want to be notified',
  'prefs.save': 'Save Preferences',
  'prefs.saving': 'Saving...',
  'prefs.saved': 'Preferences saved',
  'prefs.saveFailed': 'Failed to save preferences',
  'prefs.language.card': 'Language & Delivery',
  'prefs.language.label': 'Preferred language',
  'prefs.language.help':
    "Used for email, SMS, WhatsApp, and in-app copy. When a template isn't translated yet we fall back to English so you never miss a notification.",
  'prefs.digest.label': 'Delivery mode',
  'prefs.digest.switch':
    'Send a single hourly digest instead of per-event emails',
  'prefs.digest.help':
    'High-priority alerts (payments, disputes) always go out immediately — the digest only batches informational updates.',
  'prefs.digest.enabled': 'Digest enabled',
  'prefs.digest.disabled': 'Digest disabled',
  'prefs.language.set': 'Language set to {label}',
  'prefs.language.updateFailed': 'Failed to update language',

  // Common buttons
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.loading': 'Loading...',
};

// ----------------------------------------------------------------
// Hindi
// ----------------------------------------------------------------
const HI = {
  'nav.myProfile': 'मेरी प्रोफ़ाइल',
  'nav.myProfile.sub': 'व्यक्तिगत विवरण देखें।',
  'nav.changePassword': 'पासवर्ड बदलें',
  'nav.changePassword.sub': 'अपने लॉगिन क्रेडेंशियल्स अपडेट करें।',
  'nav.preferences': 'प्राथमिकताएँ',
  'nav.preferences.sub': 'सूचना और प्रदर्शन सेटिंग्स।',
  'nav.signOut': 'साइन आउट',
  'nav.search': 'यहाँ खोजें...',
  'nav.role.member': 'सदस्य',
  'nav.language': 'भाषा',

  'bell.title': 'सूचनाएँ',
  'bell.empty': 'कोई सूचना नहीं',
  'bell.markAllRead': 'सभी पढ़ी हुई के रूप में चिह्नित करें',
  'bell.viewAll': 'सभी सूचनाएँ देखें',
  'bell.connected': 'लाइव',
  'bell.disconnected': 'ऑफ़लाइन',
  'bell.unreadBadge': 'अपठित',

  'inbox.title': 'सूचना केंद्र',
  'inbox.subtitle': 'सभी सूचनाएँ एक जगह',
  'inbox.filter.all': 'सभी',
  'inbox.filter.unread': 'अपठित',
  'inbox.filter.read': 'पढ़ी हुई',
  'inbox.deleteSelected': 'चयनित हटाएँ',
  'inbox.selectAll': 'सभी चुनें',
  'inbox.empty': 'आपके पास कोई सूचना नहीं है',

  'prefs.title': 'सूचना प्राथमिकताएँ',
  'prefs.subtitle': 'चुनें कि आपको कैसे सूचित किया जाए',
  'prefs.save': 'प्राथमिकताएँ सहेजें',
  'prefs.saving': 'सहेजा जा रहा है...',
  'prefs.saved': 'प्राथमिकताएँ सहेजी गईं',
  'prefs.saveFailed': 'प्राथमिकताएँ सहेजने में विफल',
  'prefs.language.card': 'भाषा और वितरण',
  'prefs.language.label': 'पसंदीदा भाषा',
  'prefs.language.help':
    'ईमेल, SMS, WhatsApp और इन-ऐप सामग्री के लिए उपयोग किया जाता है। यदि अनुवाद उपलब्ध नहीं है, तो हम अंग्रेज़ी पर वापस आ जाते हैं।',
  'prefs.digest.label': 'वितरण मोड',
  'prefs.digest.switch':
    'प्रत्येक घटना के बजाय प्रति घंटे एक ही डाइजेस्ट भेजें',
  'prefs.digest.help':
    'उच्च-प्राथमिकता सूचनाएँ (भुगतान, विवाद) हमेशा तुरंत भेजी जाती हैं।',
  'prefs.digest.enabled': 'डाइजेस्ट सक्षम',
  'prefs.digest.disabled': 'डाइजेस्ट अक्षम',
  'prefs.language.set': 'भाषा {label} पर सेट की गई',
  'prefs.language.updateFailed': 'भाषा अपडेट करने में विफल',

  'common.save': 'सहेजें',
  'common.cancel': 'रद्द करें',
  'common.delete': 'हटाएँ',
  'common.close': 'बंद करें',
  'common.yes': 'हाँ',
  'common.no': 'नहीं',
  'common.loading': 'लोड हो रहा है...',
};

// ----------------------------------------------------------------
// Tamil
// ----------------------------------------------------------------
const TA = {
  'nav.myProfile': 'என் சுயவிவரம்',
  'nav.myProfile.sub': 'தனிப்பட்ட விவரங்களைக் காண்க.',
  'nav.changePassword': 'கடவுச்சொல்லை மாற்றவும்',
  'nav.changePassword.sub': 'உங்கள் உள்நுழைவு சான்றுகளைப் புதுப்பிக்கவும்.',
  'nav.preferences': 'விருப்பங்கள்',
  'nav.preferences.sub': 'அறிவிப்பு மற்றும் காட்சி அமைப்புகள்.',
  'nav.signOut': 'வெளியேறு',
  'nav.search': 'இங்கே தேடுங்கள்...',
  'nav.role.member': 'உறுப்பினர்',
  'nav.language': 'மொழி',

  'bell.title': 'அறிவிப்புகள்',
  'bell.empty': 'அறிவிப்புகள் இல்லை',
  'bell.markAllRead': 'அனைத்தையும் படித்ததாகக் குறிக்க',
  'bell.viewAll': 'அனைத்து அறிவிப்புகளையும் காண்க',
  'bell.connected': 'நேரலை',
  'bell.disconnected': 'இணைப்பில்லை',
  'bell.unreadBadge': 'படிக்கப்படாதவை',

  'inbox.title': 'அறிவிப்பு மையம்',
  'inbox.subtitle': 'அனைத்து அறிவிப்புகளும் ஒரே இடத்தில்',
  'inbox.filter.all': 'அனைத்தும்',
  'inbox.filter.unread': 'படிக்கப்படாதவை',
  'inbox.filter.read': 'படித்தவை',
  'inbox.deleteSelected': 'தேர்ந்தெடுத்தவற்றை நீக்கு',
  'inbox.selectAll': 'அனைத்தையும் தேர்ந்தெடு',
  'inbox.empty': 'உங்களிடம் அறிவிப்புகள் எதுவும் இல்லை',

  'prefs.title': 'அறிவிப்பு விருப்பங்கள்',
  'prefs.subtitle': 'எப்படி அறிவிக்கப்பட வேண்டும் என்பதைத் தேர்வு செய்யவும்',
  'prefs.save': 'விருப்பங்களைச் சேமிக்கவும்',
  'prefs.saving': 'சேமிக்கிறது...',
  'prefs.saved': 'விருப்பங்கள் சேமிக்கப்பட்டன',
  'prefs.saveFailed': 'விருப்பங்களைச் சேமிப்பதில் தோல்வி',
  'prefs.language.card': 'மொழி மற்றும் வழங்கல்',
  'prefs.language.label': 'விருப்பமான மொழி',
  'prefs.language.help':
    'மின்னஞ்சல், SMS, WhatsApp மற்றும் பயன்பாட்டிற்கான உரைக்குப் பயன்படுகிறது. மொழிபெயர்ப்பு இல்லாதபோது ஆங்கிலம் பின்னணியாகப் பயன்படுகிறது.',
  'prefs.digest.label': 'வழங்கல் முறை',
  'prefs.digest.switch':
    'ஒவ்வொரு நிகழ்விற்கும் தனித்தனியாக அனுப்பாமல் ஒவ்வொரு மணிநேரத்திற்கும் ஒரே சுருக்கம் அனுப்பவும்',
  'prefs.digest.help':
    'உயர் முன்னுரிமை எச்சரிக்கைகள் (கட்டணங்கள், பிரச்சினைகள்) எப்போதும் உடனடியாக அனுப்பப்படும்.',
  'prefs.digest.enabled': 'சுருக்கம் இயக்கப்பட்டது',
  'prefs.digest.disabled': 'சுருக்கம் முடக்கப்பட்டது',
  'prefs.language.set': 'மொழி {label} ஆக அமைக்கப்பட்டது',
  'prefs.language.updateFailed': 'மொழியைப் புதுப்பிப்பதில் தோல்வி',

  'common.save': 'சேமி',
  'common.cancel': 'ரத்து செய்',
  'common.delete': 'நீக்கு',
  'common.close': 'மூடு',
  'common.yes': 'ஆம்',
  'common.no': 'இல்லை',
  'common.loading': 'ஏற்றுகிறது...',
};

// ----------------------------------------------------------------
// Telugu
// ----------------------------------------------------------------
const TE = {
  'nav.myProfile': 'నా ప్రొఫైల్',
  'nav.myProfile.sub': 'వ్యక్తిగత వివరాలను చూడండి.',
  'nav.changePassword': 'పాస్‌వర్డ్ మార్చండి',
  'nav.changePassword.sub': 'మీ లాగిన్ వివరాలను నవీకరించండి.',
  'nav.preferences': 'ప్రాధాన్యతలు',
  'nav.preferences.sub': 'నోటిఫికేషన్ మరియు డిస్‌ప్లే సెట్టింగ్‌లు.',
  'nav.signOut': 'సైన్ అవుట్',
  'nav.search': 'ఇక్కడ వెతకండి...',
  'nav.role.member': 'సభ్యుడు',
  'nav.language': 'భాష',

  'bell.title': 'నోటిఫికేషన్‌లు',
  'bell.empty': 'నోటిఫికేషన్‌లు లేవు',
  'bell.markAllRead': 'అన్నీ చదివినట్లు గుర్తించండి',
  'bell.viewAll': 'అన్ని నోటిఫికేషన్‌లను చూడండి',
  'bell.connected': 'లైవ్',
  'bell.disconnected': 'ఆఫ్‌లైన్',
  'bell.unreadBadge': 'చదవనివి',

  'inbox.title': 'నోటిఫికేషన్ కేంద్రం',
  'inbox.subtitle': 'అన్ని నోటిఫికేషన్‌లు ఒకే చోట',
  'inbox.filter.all': 'అన్నీ',
  'inbox.filter.unread': 'చదవనివి',
  'inbox.filter.read': 'చదివినవి',
  'inbox.deleteSelected': 'ఎంచుకున్నవి తొలగించు',
  'inbox.selectAll': 'అన్నీ ఎంచుకో',
  'inbox.empty': 'మీకు నోటిఫికేషన్‌లు లేవు',

  'prefs.title': 'నోటిఫికేషన్ ప్రాధాన్యతలు',
  'prefs.subtitle': 'ఎలా తెలియజేయాలో ఎంచుకోండి',
  'prefs.save': 'ప్రాధాన్యతలను సేవ్ చేయండి',
  'prefs.saving': 'సేవ్ చేస్తోంది...',
  'prefs.saved': 'ప్రాధాన్యతలు సేవ్ చేయబడ్డాయి',
  'prefs.saveFailed': 'సేవ్ చేయడంలో విఫలం',
  'prefs.language.card': 'భాష మరియు డెలివరీ',
  'prefs.language.label': 'ఇష్టపడే భాష',
  'prefs.language.help':
    'ఇమెయిల్, SMS, WhatsApp మరియు ఇన్-యాప్ కంటెంట్‌కు ఉపయోగించబడుతుంది. అనువాదం అందుబాటులో లేనప్పుడు ఇంగ్లీషుకు తిరిగి వెళ్తుంది.',
  'prefs.digest.label': 'డెలివరీ మోడ్',
  'prefs.digest.switch':
    'ప్రతి ఈవెంట్‌కు బదులు గంటకు ఒకసారి సారాంశం పంపండి',
  'prefs.digest.help':
    'అధిక ప్రాధాన్యత అప్రమత్తతలు (చెల్లింపులు, వివాదాలు) ఎల్లప్పుడూ వెంటనే పంపబడతాయి.',
  'prefs.digest.enabled': 'డైజెస్ట్ ప్రారంభించబడింది',
  'prefs.digest.disabled': 'డైజెస్ట్ నిలిపివేయబడింది',
  'prefs.language.set': 'భాష {label}కి సెట్ చేయబడింది',
  'prefs.language.updateFailed': 'భాషను నవీకరించడంలో విఫలం',

  'common.save': 'సేవ్ చేయండి',
  'common.cancel': 'రద్దు చేయండి',
  'common.delete': 'తొలగించు',
  'common.close': 'మూసివేయి',
  'common.yes': 'అవును',
  'common.no': 'లేదు',
  'common.loading': 'లోడ్ అవుతోంది...',
};

// ----------------------------------------------------------------
// Kannada
// ----------------------------------------------------------------
const KN = {
  'nav.myProfile': 'ನನ್ನ ಪ್ರೊಫೈಲ್',
  'nav.myProfile.sub': 'ವೈಯಕ್ತಿಕ ವಿವರಗಳನ್ನು ವೀಕ್ಷಿಸಿ.',
  'nav.changePassword': 'ಪಾಸ್‌ವರ್ಡ್ ಬದಲಿಸಿ',
  'nav.changePassword.sub': 'ನಿಮ್ಮ ಲಾಗಿನ್ ರುಜುವಾತುಗಳನ್ನು ನವೀಕರಿಸಿ.',
  'nav.preferences': 'ಆದ್ಯತೆಗಳು',
  'nav.preferences.sub': 'ಅಧಿಸೂಚನೆ ಮತ್ತು ಪ್ರದರ್ಶನ ಸೆಟ್ಟಿಂಗ್‌ಗಳು.',
  'nav.signOut': 'ಸೈನ್ ಔಟ್',
  'nav.search': 'ಇಲ್ಲಿ ಹುಡುಕಿ...',
  'nav.role.member': 'ಸದಸ್ಯ',
  'nav.language': 'ಭಾಷೆ',

  'bell.title': 'ಅಧಿಸೂಚನೆಗಳು',
  'bell.empty': 'ಯಾವುದೇ ಅಧಿಸೂಚನೆಗಳಿಲ್ಲ',
  'bell.markAllRead': 'ಎಲ್ಲವನ್ನೂ ಓದಿದಂತೆ ಗುರುತಿಸಿ',
  'bell.viewAll': 'ಎಲ್ಲಾ ಅಧಿಸೂಚನೆಗಳನ್ನು ವೀಕ್ಷಿಸಿ',
  'bell.connected': 'ಲೈವ್',
  'bell.disconnected': 'ಆಫ್‌ಲೈನ್',
  'bell.unreadBadge': 'ಓದದ',

  'inbox.title': 'ಅಧಿಸೂಚನೆ ಕೇಂದ್ರ',
  'inbox.subtitle': 'ಎಲ್ಲಾ ಅಧಿಸೂಚನೆಗಳು ಒಂದೇ ಸ್ಥಳದಲ್ಲಿ',
  'inbox.filter.all': 'ಎಲ್ಲಾ',
  'inbox.filter.unread': 'ಓದದ',
  'inbox.filter.read': 'ಓದಿದ',
  'inbox.deleteSelected': 'ಆಯ್ಕೆಮಾಡಿದ್ದನ್ನು ಅಳಿಸಿ',
  'inbox.selectAll': 'ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ',
  'inbox.empty': 'ನಿಮಗೆ ಯಾವುದೇ ಅಧಿಸೂಚನೆಗಳಿಲ್ಲ',

  'prefs.title': 'ಅಧಿಸೂಚನೆ ಆದ್ಯತೆಗಳು',
  'prefs.subtitle': 'ನಿಮಗೆ ಹೇಗೆ ತಿಳಿಸಬೇಕೆಂದು ಆಯ್ಕೆಮಾಡಿ',
  'prefs.save': 'ಆದ್ಯತೆಗಳನ್ನು ಉಳಿಸಿ',
  'prefs.saving': 'ಉಳಿಸಲಾಗುತ್ತಿದೆ...',
  'prefs.saved': 'ಆದ್ಯತೆಗಳನ್ನು ಉಳಿಸಲಾಗಿದೆ',
  'prefs.saveFailed': 'ಉಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
  'prefs.language.card': 'ಭಾಷೆ ಮತ್ತು ವಿತರಣೆ',
  'prefs.language.label': 'ಆದ್ಯತೆಯ ಭಾಷೆ',
  'prefs.language.help':
    'ಇಮೇಲ್, SMS, WhatsApp ಮತ್ತು ಅಪ್ಲಿಕೇಶನ್ ವಿಷಯಕ್ಕೆ ಬಳಸಲಾಗುತ್ತದೆ. ಅನುವಾದ ಲಭ್ಯವಿಲ್ಲದಿದ್ದಾಗ ಇಂಗ್ಲಿಷ್‌ಗೆ ಹಿಂತಿರುಗುತ್ತದೆ.',
  'prefs.digest.label': 'ವಿತರಣಾ ಮೋಡ್',
  'prefs.digest.switch':
    'ಪ್ರತಿ ಈವೆಂಟ್‌ಗೆ ಬದಲಾಗಿ ಗಂಟೆಗೊಮ್ಮೆ ಸಾರಾಂಶವನ್ನು ಕಳುಹಿಸಿ',
  'prefs.digest.help':
    'ಉನ್ನತ ಆದ್ಯತೆಯ ಎಚ್ಚರಿಕೆಗಳು (ಪಾವತಿಗಳು, ವಿವಾದಗಳು) ಯಾವಾಗಲೂ ತಕ್ಷಣ ಕಳುಹಿಸಲ್ಪಡುತ್ತವೆ.',
  'prefs.digest.enabled': 'ಡೈಜೆಸ್ಟ್ ಸಕ್ರಿಯಗೊಳಿಸಲಾಗಿದೆ',
  'prefs.digest.disabled': 'ಡೈಜೆಸ್ಟ್ ನಿಷ್ಕ್ರಿಯಗೊಳಿಸಲಾಗಿದೆ',
  'prefs.language.set': 'ಭಾಷೆಯನ್ನು {label}ಗೆ ಹೊಂದಿಸಲಾಗಿದೆ',
  'prefs.language.updateFailed': 'ಭಾಷೆಯನ್ನು ನವೀಕರಿಸಲು ವಿಫಲವಾಗಿದೆ',

  'common.save': 'ಉಳಿಸಿ',
  'common.cancel': 'ರದ್ದುಮಾಡಿ',
  'common.delete': 'ಅಳಿಸಿ',
  'common.close': 'ಮುಚ್ಚಿ',
  'common.yes': 'ಹೌದು',
  'common.no': 'ಇಲ್ಲ',
  'common.loading': 'ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
};

// ----------------------------------------------------------------
// Marathi
// ----------------------------------------------------------------
const MR = {
  'nav.myProfile': 'माझे प्रोफाइल',
  'nav.myProfile.sub': 'वैयक्तिक तपशील पहा.',
  'nav.changePassword': 'पासवर्ड बदला',
  'nav.changePassword.sub': 'आपले लॉगिन क्रेडेन्शियल अद्यतनित करा.',
  'nav.preferences': 'प्राधान्ये',
  'nav.preferences.sub': 'सूचना आणि प्रदर्शन सेटिंग्ज.',
  'nav.signOut': 'साइन आउट',
  'nav.search': 'येथे शोधा...',
  'nav.role.member': 'सदस्य',
  'nav.language': 'भाषा',

  'bell.title': 'सूचना',
  'bell.empty': 'कोणत्याही सूचना नाहीत',
  'bell.markAllRead': 'सर्व वाचलेले म्हणून चिन्हांकित करा',
  'bell.viewAll': 'सर्व सूचना पहा',
  'bell.connected': 'थेट',
  'bell.disconnected': 'ऑफलाइन',
  'bell.unreadBadge': 'न वाचलेल्या',

  'inbox.title': 'सूचना केंद्र',
  'inbox.subtitle': 'सर्व सूचना एका ठिकाणी',
  'inbox.filter.all': 'सर्व',
  'inbox.filter.unread': 'न वाचलेल्या',
  'inbox.filter.read': 'वाचलेल्या',
  'inbox.deleteSelected': 'निवडलेले हटवा',
  'inbox.selectAll': 'सर्व निवडा',
  'inbox.empty': 'आपल्याकडे कोणत्याही सूचना नाहीत',

  'prefs.title': 'सूचना प्राधान्ये',
  'prefs.subtitle': 'तुम्हाला कसे सूचित केले जावे ते निवडा',
  'prefs.save': 'प्राधान्ये जतन करा',
  'prefs.saving': 'जतन होत आहे...',
  'prefs.saved': 'प्राधान्ये जतन केली',
  'prefs.saveFailed': 'जतन करण्यात अयशस्वी',
  'prefs.language.card': 'भाषा आणि वितरण',
  'prefs.language.label': 'पसंतीची भाषा',
  'prefs.language.help':
    'ईमेल, SMS, WhatsApp आणि ॲप-मधील मजकुरासाठी वापरले जाते. भाषांतर उपलब्ध नसल्यास इंग्रजीवर परत येते.',
  'prefs.digest.label': 'वितरण मोड',
  'prefs.digest.switch':
    'प्रत्येक इव्हेंटऐवजी दर तासाला एकच सारांश पाठवा',
  'prefs.digest.help':
    'उच्च प्राधान्य सूचना (देयके, विवाद) नेहमी त्वरित पाठवल्या जातात.',
  'prefs.digest.enabled': 'डायजेस्ट सक्षम केले',
  'prefs.digest.disabled': 'डायजेस्ट अक्षम केले',
  'prefs.language.set': 'भाषा {label} वर सेट केली',
  'prefs.language.updateFailed': 'भाषा अद्यतनित करण्यात अयशस्वी',

  'common.save': 'जतन करा',
  'common.cancel': 'रद्द करा',
  'common.delete': 'हटवा',
  'common.close': 'बंद करा',
  'common.yes': 'होय',
  'common.no': 'नाही',
  'common.loading': 'लोड होत आहे...',
};

// ----------------------------------------------------------------
// Bengali
// ----------------------------------------------------------------
const BN = {
  'nav.myProfile': 'আমার প্রোফাইল',
  'nav.myProfile.sub': 'ব্যক্তিগত বিবরণ দেখুন।',
  'nav.changePassword': 'পাসওয়ার্ড পরিবর্তন করুন',
  'nav.changePassword.sub': 'আপনার লগইন তথ্য আপডেট করুন।',
  'nav.preferences': 'পছন্দসমূহ',
  'nav.preferences.sub': 'নোটিফিকেশন এবং প্রদর্শন সেটিংস।',
  'nav.signOut': 'সাইন আউট',
  'nav.search': 'এখানে অনুসন্ধান করুন...',
  'nav.role.member': 'সদস্য',
  'nav.language': 'ভাষা',

  'bell.title': 'নোটিফিকেশন',
  'bell.empty': 'কোন নোটিফিকেশন নেই',
  'bell.markAllRead': 'সব পড়া হিসাবে চিহ্নিত করুন',
  'bell.viewAll': 'সমস্ত নোটিফিকেশন দেখুন',
  'bell.connected': 'লাইভ',
  'bell.disconnected': 'অফলাইন',
  'bell.unreadBadge': 'অপঠিত',

  'inbox.title': 'নোটিফিকেশন কেন্দ্র',
  'inbox.subtitle': 'সব নোটিফিকেশন এক জায়গায়',
  'inbox.filter.all': 'সব',
  'inbox.filter.unread': 'অপঠিত',
  'inbox.filter.read': 'পড়া',
  'inbox.deleteSelected': 'নির্বাচিতগুলি মুছুন',
  'inbox.selectAll': 'সব নির্বাচন করুন',
  'inbox.empty': 'আপনার কোন নোটিফিকেশন নেই',

  'prefs.title': 'নোটিফিকেশন পছন্দসমূহ',
  'prefs.subtitle': 'কিভাবে আপনাকে জানানো হবে তা বেছে নিন',
  'prefs.save': 'পছন্দসমূহ সংরক্ষণ করুন',
  'prefs.saving': 'সংরক্ষণ করা হচ্ছে...',
  'prefs.saved': 'পছন্দসমূহ সংরক্ষিত হয়েছে',
  'prefs.saveFailed': 'সংরক্ষণ করতে ব্যর্থ',
  'prefs.language.card': 'ভাষা ও বিতরণ',
  'prefs.language.label': 'পছন্দের ভাষা',
  'prefs.language.help':
    'ইমেল, SMS, WhatsApp এবং ইন-অ্যাপ কন্টেন্টের জন্য ব্যবহৃত হয়। অনুবাদ উপলব্ধ না হলে ইংরেজিতে ফিরে যায়।',
  'prefs.digest.label': 'বিতরণ মোড',
  'prefs.digest.switch':
    'প্রতিটি ইভেন্টের পরিবর্তে প্রতি ঘন্টায় একটি সারসংক্ষেপ পাঠান',
  'prefs.digest.help':
    'উচ্চ-অগ্রাধিকার সতর্কতা (পেমেন্ট, বিরোধ) সর্বদা অবিলম্বে পাঠানো হয়।',
  'prefs.digest.enabled': 'ডাইজেস্ট সক্রিয়',
  'prefs.digest.disabled': 'ডাইজেস্ট নিষ্ক্রিয়',
  'prefs.language.set': 'ভাষা {label}-এ সেট করা হয়েছে',
  'prefs.language.updateFailed': 'ভাষা আপডেট করতে ব্যর্থ',

  'common.save': 'সংরক্ষণ করুন',
  'common.cancel': 'বাতিল করুন',
  'common.delete': 'মুছুন',
  'common.close': 'বন্ধ করুন',
  'common.yes': 'হ্যাঁ',
  'common.no': 'না',
  'common.loading': 'লোড হচ্ছে...',
};

// Merge the per-surface overlays in (imported at the top of this
// file). Keeping them in separate files keeps this module readable
// — add another import like that for each new translated surface.
const merge = (...layers) =>
  Object.assign({}, ...layers.map((l) => l || {}));

// Chrome + sidebar dictionaries only — small, safe to ship in the main
// bundle. Page-level copy is layered in at runtime (see loadPageOverlays).
export const TRANSLATIONS = {
  EN: merge(EN, sidebarOverlays.EN),
  HI: merge(HI, sidebarOverlays.HI),
  TA: merge(TA, sidebarOverlays.TA),
  TE: merge(TE, sidebarOverlays.TE),
  KN: merge(KN, sidebarOverlays.KN),
  MR: merge(MR, sidebarOverlays.MR),
  BN: merge(BN, sidebarOverlays.BN),
};

// Async-load the heavy per-page overlay as its own chunk. Returns the
// ``{ EN, HI, ... }`` overlay map. Callers merge it over TRANSLATIONS at
// runtime; failure is non-fatal (English inline fallbacks remain).
export const loadPageOverlays = () =>
  import('./page_translations').then((m) => m.default);

// Languages the dropdown offers. Kept in a simple array so a future
// admin toggle ("disable TA for now") is a one-line change. Labels
// render in the native script so the user picks by recognising
// their own language even on a device set to English.
export const LANGUAGE_OPTIONS = [
  { code: 'EN', label: 'English',            short: 'EN' },
  { code: 'HI', label: 'हिन्दी',               short: 'हि' },
  { code: 'TA', label: 'தமிழ்',                short: 'த' },
  { code: 'TE', label: 'తెలుగు',               short: 'తె' },
  { code: 'KN', label: 'ಕನ್ನಡ',               short: 'ಕ' },
  { code: 'MR', label: 'मराठी',                short: 'म' },
  { code: 'BN', label: 'বাংলা',               short: 'বা' },
];
